import { useMemo } from 'react';

import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import { RESTClient } from 'src/apis/service';
import { MatchService } from 'src/apis/service/MatchService';
import useLogin from 'src/hooks/useLogin';
import { MatchFilter, MatchFilterId, MatchFilterSelection } from 'src/types/Match';
import { MINUTE_MS } from 'src/utils/time';

export function useListMatchFiltersQuery(
  options?: Omit<
    UseQueryOptions<MatchFilter[], unknown, MatchFilter[], string[]>,
    'queryKey' | 'queryFn' | 'enabled'
  >
) {
  const { isLogin } = useLogin();

  return useQuery({
    queryKey: ['matchFilters'],
    async queryFn() {
      const service = new MatchService(new RESTClient());

      return service.listMatchFilters();
    },
    enabled: isLogin,
    ...options,
  });
}

export function useMatchFilterById(
  filterIds: MatchFilterId | MatchFilterId[]
): MatchFilter | undefined {
  const { data: [matchFilter] = [] } = useListMatchFiltersQuery({
    select(matchFilters) {
      return matchFilters.filter((filter) => [filterIds].flat().includes(filter.filterId));
    },
  });

  return matchFilter;
}

/**
 * 매치 필터 별 원활도를 조회해서 FilterDensityDisplay 의 렌더에 필요한 데이터를 도출합니다.
 *
 * @param filterId 필터 ID
 * @param filterSelection 원활도를 조회하고 싶은 Filter 선택
 * @param filterSelectionField Filter Type 에 따른 optionKey getter field
 * @returns
 */
export function useMatchPoolStatus(
  filterId: MatchFilterId | MatchFilterId[],
  filterSelection: MatchFilterSelection | null,
  filterSelectionField: Extract<
    keyof MatchFilterSelection,
    'rangeOptionKeys' | 'multiSelectOptionKeys'
  >
) {
  const filterIds = useMemo(() => [filterId].flat().sort(), [filterId]);

  const { isLogin } = useLogin();

  const predicates: Record<
    typeof filterSelectionField,
    (filterOption: Pick<MatchFilterSelection, 'optionKey'>) => boolean
  > = {
    rangeOptionKeys: ({ optionKey }) =>
      filterSelection?.rangeOptionKeys?.maxKey === optionKey ||
      filterSelection?.rangeOptionKeys?.minKey === optionKey,
    multiSelectOptionKeys: ({ optionKey }) =>
      filterSelection?.multiSelectOptionKeys?.includes(optionKey ?? '') ?? false,
  };

  const { data: poolStatuses = {} } = useQuery({
    queryKey: ['matchPoolStatus', ...filterIds],
    async queryFn() {
      const service = new MatchService(new RESTClient());
      return service.listMatchFilterPoolStatus({ filterIds });
    },
    enabled: isLogin && filterSelection != null,
    select(response) {
      const result = Object.fromEntries(
        response.filters.flatMap(({ filterOptions }) =>
          filterOptions
            .filter(predicates[filterSelectionField])
            .map(({ optionKey, poolStatusIndex }) => [
              optionKey,
              response.poolStatus[poolStatusIndex],
            ])
        )
      );

      return result;
    },
    /**
     * 서버의 캐시 TTL인 1분에 맞춥니다.
     *
     * @see {https://www.notion.so/hpcnt/1c2ce00cd3548002ad7acbad90297116?pvs=4}
     **/
    staleTime: MINUTE_MS,
    gcTime: MINUTE_MS,
    retry: false,
  });

  return { poolStatuses };
}
